import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { AuthContext } from '../auth';
import { client } from '../api';
import { NavSidebar } from './NavSidebar';
import { TopNavbar } from './TopNavbar/TopNavbar';
import Banner, { BannerProps } from 'design-system/src/components/Banner/Banner';
import { throttle } from 'lodash';
import { DefaultFiltersNewFeatureModal } from './DefaultFiltersNewFeatureModal';
import { useNavigate } from 'react-router-dom';
import { PageContainer as DesignSystemPageContainer } from 'design-system/src/components/PageContainer';
import { useGetProductNotification } from '../hooks/useGetProductNotification';
import FaxVerificationSuccessNotification from './productNotifications/FaxVerificationSuccessNotification';
import { ProductNotificationType } from '../types';

// Variable for tracking the current new feature modal component; will return
// `null` while there are no features to announce using this modal
const NewFeatureModalComponent = DefaultFiltersNewFeatureModal;

const BannersContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  // TODO: add z-index list
  z-index: 3;
`;

const MainContentContainer = styled.div<{
  $background: Background;
  $navbarPlacement: NavbarPlacement;
  $noPadding?: boolean;
  $bannersHeight: number;
}>`
  flex: 1;
  ${({ $noPadding }) => ($noPadding ? null : 'padding: 1.25rem;')}
  margin-left: ${({ $navbarPlacement }) => ($navbarPlacement === 'top' ? '0;' : 'var(--nav-sidebar-width)')};
  background-color: ${({ $background }) => ($background === 'light' ? 'var(--surface-100);' : 'var(--surface-50)')};
  margin-top: ${({ $bannersHeight }) => $bannersHeight}px;
`;

const MainContent = styled.div<{ $width: 'full' | 'fitContent' }>`
  margin: 0 auto;
  max-width: ${({ $width }) => ($width === 'full' ? '100%' : 'var(--main-content-width)')};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

// light is used for logged out pages and extraLight is logged in pages
type Background = 'extraLight' | 'light';
type NavbarPlacement = 'side' | 'top';

const NOTIFICATION_TYPE_MAP: Record<ProductNotificationType, React.ComponentType<{ productNotificationId: string }>> = {
  fax_verification_success: FaxVerificationSuccessNotification,
};

interface PageContainerProps {
  title?: string;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  onBack?: () => void;
  centered?: boolean;
  background?: Background;
  navbarPlacement?: NavbarPlacement;
  // Removes padding from main content
  noPadding?: boolean;
  width?: 'full' | 'fitContent';
  // content that appears on the right side of the header
  headerRight?: React.ReactNode;
}

const PageContainer: React.FC<PageContainerProps> = ({
  title,
  subtitle,
  children,
  background = 'extraLight',
  navbarPlacement = 'side',
  noPadding,
  width = 'full',
  headerRight,
}) => {
  const authContext = useContext(AuthContext);
  const [newFeatureModalVisible, setNewFeatureModalVisible] = useState(false);
  const [bannerData, setBannerData] = useState<
    Array<BannerProps & { dismissible?: boolean; dismiss_endpoint?: string }>
  >([]);
  const [bannersHeight, setBannersHeight] = useState(0);
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const bannersContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const getBannersContainerHeight = () => {
    if (bannersContainerRef.current) {
      const bannersHeight = bannersContainerRef.current.clientHeight;
      setBannersHeight(bannersHeight);
    }
  };

  const throttledGetBannersContainerHeight = throttle(getBannersContainerHeight, 1000);

  useEffect(() => {
    window.addEventListener('resize', throttledGetBannersContainerHeight);

    return () => {
      window.removeEventListener('resize', throttledGetBannersContainerHeight);
    };
    // TODO(WIT-4362): fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    throttledGetBannersContainerHeight();
    // TODO(WIT-4362): fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  useEffect(() => {
    if (authContext.user?.new_feature_modal?.should_show && !newFeatureModalVisible) {
      setNewFeatureModalVisible(true);
    }
    if (!authContext.user?.new_feature_modal?.should_show && newFeatureModalVisible) {
      setNewFeatureModalVisible(false);
    }

    setBannerData(authContext.user?.banners ?? []);
  }, [authContext, newFeatureModalVisible]);

  useEffect(() => {
    throttledGetBannersContainerHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData]);

  const submitNewFeatureModalView = useMutation({
    mutationFn: async () => {
      if (!authContext.user) return;

      authContext.setUser({
        ...authContext.user,
        new_feature_modal: {
          ...authContext.user.new_feature_modal,
          should_show: false,
        },
      });

      return client
        .post('/new-feature-modal-view', {
          type: authContext.user.new_feature_modal.type,
        })
        .then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });
    },
  });

  const { data: productNotification } = useGetProductNotification(authContext.auth);

  let productNotificationToShow: React.ReactNode | null = null;
  if (productNotification?.product_notification_to_show && productNotification.product_notification_id) {
    const NotificationComponent = NOTIFICATION_TYPE_MAP[productNotification.product_notification_to_show];
    if (NotificationComponent) {
      productNotificationToShow = (
        <NotificationComponent productNotificationId={productNotification.product_notification_id} />
      );
    }
  }

  const { data: notificationsData } = useQuery<{
    num_open_pa_reviews: number;
    num_tasks_requiring_response: number;
    num_portal_notifications: number;
  }>({
    queryKey: ['notifications'],
    // should not fetch notifications if not logged in
    enabled: authContext.auth,
    queryFn: () => {
      return client.get(`notifications`).then((res) => res.data);
    },
  });

  useEffect(() => {
    // we're updating authContext in a useEffect because useQuery doesn't have an onSuccess callback in this version
    // of react-query, so using useEffect is the closest approximation of that behavior
    if (notificationsData && authContext.user) {
      authContext.setUser({
        ...authContext.user,
        notifications: notificationsData,
      });
    }
    // TODO(WIT-4362): fix
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsData]);

  return (
    <>
      <BannersContainer ref={bannersContainerRef}>
        {bannerData.map((banner) => {
          const dismissEndpoint = banner.dismiss_endpoint;
          const bannerProps: BannerProps = {
            ...banner,
            onDismiss:
              banner.dismissible && dismissEndpoint
                ? () => {
                    // Optimistically remove the banner immediately
                    setBannerData((currentBanners) => currentBanners.filter((b) => b.title !== banner.title));

                    // Make the API call in the background
                    client.post(dismissEndpoint).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['checkLogin'] });
                    });
                  }
                : undefined,
          };
          return <Banner key={banner.title} {...bannerProps} />;
        })}
      </BannersContainer>

      {productNotificationToShow}

      <DesignSystemPageContainer
        $background={background}
        $navbarPlacement={navbarPlacement}
        $bannersHeight={bannersHeight}
        ref={containerRef}
      >
        {navbarPlacement === 'side' ? <NavSidebar bannersHeight={bannersHeight} /> : <TopNavbar />}
        <MainContentContainer
          $background={background}
          $navbarPlacement={navbarPlacement}
          $noPadding={noPadding}
          $bannersHeight={bannersHeight}
        >
          <MainContent $width={width}>
            {(title || headerRight) && (
              <Header>
                <TitleContainer>
                  {title && (
                    <Typography styledAs="h6" renderedAs="h1">
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography styledAs="bodySmallSpaceGrotesk" color="var(--grayscale-600)">
                      {subtitle}
                    </Typography>
                  )}
                </TitleContainer>
                {headerRight}
              </Header>
            )}
            {children}
          </MainContent>
        </MainContentContainer>
      </DesignSystemPageContainer>

      {NewFeatureModalComponent && newFeatureModalVisible && (
        <NewFeatureModalComponent
          onClose={() => {
            setNewFeatureModalVisible(false);
            submitNewFeatureModalView.mutate();
          }}
          onLinkClick={async () => {
            try {
              // Wait for the mutation to complete before navigating to settings
              // or else the modal will just pop back up again after navigating
              await submitNewFeatureModalView.mutateAsync();
            } catch (error) {
              console.error('Failed to submit new feature modal view:', error);
            } finally {
              // Still close the modal and navigate even if the mutation fails
              navigate('/settings');
            }
          }}
        />
      )}
    </>
  );
};

export default PageContainer;
